/**
 * スレッドフィルターコンポーネント
 * スレッド一覧のフィルタリング・ソート機能
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Archive as ArchiveIcon,
  Block as BlockIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import { ThreadFilters as ThreadFiltersType } from '../../types/message-thread';

interface ThreadFiltersProps {
  filters: ThreadFiltersType;
  onFiltersChange: (filters: Partial<ThreadFiltersType>) => void;
  userType: 'company' | 'student';
}

export const ThreadFilters: React.FC<ThreadFiltersProps> = ({
  filters,
  onFiltersChange,
  userType,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || '');

  useEffect(() => {
    setLocalSearch(filters.search || '');
  }, [filters.search]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    const trimmed = localSearch.trim();
    onFiltersChange({ search: trimmed ? trimmed : undefined });
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    onFiltersChange({ search: undefined });
  };

  const handleFilterChange = (filterType: string) => {
    // 既存のフィルターをリセット
    const resetFilters = {
      includeArchived: false,
      includeBlocked: false,
      onlyArchived: false,
      onlyBlocked: false,
    };

    switch (filterType) {
      case 'all':
        onFiltersChange({
          ...resetFilters,
          includeArchived: true,
          includeBlocked: userType === 'student', // 学生のみブロック機能を使用
        });
        break;
      case 'active':
        onFiltersChange(resetFilters);
        break;
      case 'archived':
        onFiltersChange({
          ...resetFilters,
          onlyArchived: true,
        });
        break;
      case 'blocked':
        onFiltersChange({
          ...resetFilters,
          onlyBlocked: true,
        });
        break;
    }
  };

  const getCurrentFilterType = () => {
    if (filters.onlyArchived) return 'archived';
    if (filters.onlyBlocked) return 'blocked';
    if (
      filters.includeArchived &&
      (userType === 'student' ? filters.includeBlocked : true)
    )
      return 'all';
    return 'active';
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (getCurrentFilterType() !== 'active') count++;
    return count;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack spacing={2}>
        {/* 検索フィールド */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="メッセージを検索..."
            value={localSearch}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            fullWidth
            InputProps={{
              endAdornment:
                filters.search && filters.search.length > 0 ? (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ mr: 1 }}
                    aria-label="検索クリア"
                  >
                    <ClearIcon />
                  </IconButton>
                ) : undefined,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchSubmit}
            sx={{ minWidth: 88, whiteSpace: 'nowrap' }}
          >
            検索
          </Button>
        </Box>

        {/* フィルターチップ */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            表示フィルター
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={<InboxIcon />}
              label="アクティブ"
              variant={
                getCurrentFilterType() === 'active' ? 'filled' : 'outlined'
              }
              onClick={() => handleFilterChange('active')}
              size="small"
            />
            <Chip
              icon={<ArchiveIcon />}
              label="アーカイブ済み"
              variant={
                getCurrentFilterType() === 'archived' ? 'filled' : 'outlined'
              }
              onClick={() => handleFilterChange('archived')}
              size="small"
            />
            {userType === 'student' && (
              <Chip
                icon={<BlockIcon />}
                label="ブロック済み"
                variant={
                  getCurrentFilterType() === 'blocked' ? 'filled' : 'outlined'
                }
                onClick={() => handleFilterChange('blocked')}
                size="small"
              />
            )}
            <Chip
              label="すべて"
              variant={getCurrentFilterType() === 'all' ? 'filled' : 'outlined'}
              onClick={() => handleFilterChange('all')}
              size="small"
            />
          </Stack>
        </Box>

        {/* アクティブフィルター数表示 */}
        {getActiveFiltersCount() > 0 && (
          <Typography variant="caption" color="primary">
            {getActiveFiltersCount()}個のフィルターが適用されています
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
